import AddIcon from "@mui/icons-material/Add";
import { Button, DialogActions, DialogContent, DialogTitle, Modal, ModalDialog, Textarea, Typography } from "@mui/joy";
import React from "react";
import WrappedButton from "../WrappedButton";

interface ProposalTrackingNote {
	proposal_tracking_note_id: string;
	invoice_header_id: string;
	note_date: string;
	note_content: string;
	created_by_name: string;
}

interface ProposalTrackingNotesDialogProps {
	open: boolean;
	invoiceHeaderId: string;
	proposalNumber: string;
	buildingName: string;
	onClose: () => void;
	onSaved?: () => void;
}

export default function ProposalTrackingNotesDialog(props: ProposalTrackingNotesDialogProps) {
	const [notes, setNotes] = React.useState<Array<ProposalTrackingNote>>([]);
	const [noteContent, setNoteContent] = React.useState("");
	const [isLoading, setIsLoading] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);

	const loadNotes = React.useCallback(() => {
		if (!props.invoiceHeaderId) return;
		setIsLoading(true);
		fetch("Proposal/GetProposalTrackingNotes", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ invoiceHeaderId: props.invoiceHeaderId }),
		})
			.then((x) => x.json())
			.then((x) => {
				setNotes(x);
				setIsLoading(false);
			});
	}, [props.invoiceHeaderId]);

	React.useEffect(() => {
		if (props.open && props.invoiceHeaderId) {
			setNoteContent("");
			loadNotes();
		}
	}, [props.open, props.invoiceHeaderId, loadNotes]);

	const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

	const saveNote = () => {
		const trimmed = noteContent.trim();
		if (!trimmed) return;
		setIsSaving(true);
		fetch("Proposal/SaveProposalTrackingNote", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ invoiceHeaderId: props.invoiceHeaderId, noteContent: trimmed }),
		}).then(() => {
			setNoteContent("");
			setIsSaving(false);
			loadNotes();
			props.onSaved?.();
		});
	};

	return (
		<Modal open={props.open}>
			<ModalDialog sx={{ width: 700, maxHeight: "80vh", overflow: "auto" }}>
				<DialogTitle>
					Notes - {props.proposalNumber} ({props.buildingName})
				</DialogTitle>
				<DialogContent>
					<Textarea
						minRows={3}
						placeholder="Add a note..."
						value={noteContent}
						onChange={(e) => setNoteContent(e.target.value)}
						sx={{ mb: 2 }}
					/>
					<WrappedButton onClick={saveNote} busy={isSaving} disabled={!noteContent.trim()} startDecorator={<AddIcon />}>
						Add Note
					</WrappedButton>
					<Typography level="title-sm" sx={{ mt: 2, mb: 1 }}>
						History
					</Typography>
					{isLoading ? (
						<Typography>Loading...</Typography>
					) : notes.length === 0 ? (
						<Typography level="body-sm">No notes found.</Typography>
					) : (
						notes.map((record) => (
							<Typography key={record.proposal_tracking_note_id} level="body-sm" sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
								{formatDate(record.note_date)} – {record.created_by_name} – {record.note_content}
							</Typography>
						))
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={props.onClose} color="primary">
						Close
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
